<!--
  The file header's Reviewed checkbox: the open file's review status as one
  box (the status the file list's circle shows). Checked when every
  changed line is reviewed, indeterminate when some are, unchecked when
  none is. Ticking it, from none or from some, asks to mark the whole
  file; unticking asks to clear the file's marks. The box shows the file's
  status, never the click: it is set back to the file at the click and
  after the answer, so a change that was not taken leaves it as it was.
  With nothing to tick, or where marks cannot be kept, it is off but still
  reached by Tab, and says why.
-->
<script lang="ts">
  import type { Status } from '../review/model';
  import { tick } from 'svelte';
  import PendingTag from './PendingTag.svelte';

  interface Props {
    status: Status;
    /** Mark every changed line of the file reviewed (true) or clear its marks; resolves false when nothing was changed. */
    onreview: (reviewed: boolean) => Promise<boolean>;
    /** The file's marks are on their way to the server. */
    saving?: boolean;
    /** Why review marks cannot be taken here, or null. */
    offText?: string | null;
    /** The element that says how much of the file is reviewed ("3/11 lines reviewed"). */
    describedby?: string;
  }
  let { status, onreview, saving = false, offText = null, describedby }: Props = $props();

  const checked = $derived(status === 'reviewed');
  const indeterminate = $derived(status === 'partial');
  /** Why it is off: marks cannot be kept here, the file has no changed line, or its changes are not known yet. Null while it can be ticked. */
  const why = $derived(
    offText ?? (status === 'empty' ? 'This file has no changed lines to review.' : status === 'unknown' ? 'Still working out this file’s changes.' : null),
  );
  const title = $derived(
    why ??
      (checked
        ? 'Every change in this file is reviewed. Untick to remove its review marks.'
        : indeterminate
          ? 'Some of this file is reviewed. Tick to mark every change in it reviewed.'
          : 'Mark every change in this file reviewed.'),
  );
  const uid = $props.id();

  let box: HTMLInputElement | undefined = $state();

  /** The box as the file is. */
  function show() {
    if (!box) return;
    box.checked = checked;
    box.indeterminate = indeterminate;
  }

  async function change() {
    if (!box) return;
    // The browser has flipped the box. It shows the file, so it is put back at once, and the marks move it.
    const asked = box.checked;
    show();
    await onreview(asked);
    await tick();
    show();
  }
</script>

<label class="review-toggle file-reviewed" class:unavailable={why !== null} class:is-pending={saving} aria-busy={saving ? 'true' : undefined} {title}>
  {#if saving}<PendingTag label="Saving" />{/if}
  <input
    bind:this={box}
    type="checkbox"
    aria-label="Reviewed, the whole file"
    aria-describedby={[describedby, why === null ? null : `${uid}-why`].filter(Boolean).join(' ') || undefined}
    aria-disabled={why === null ? undefined : 'true'}
    {checked}
    {indeterminate}
    onclick={(e) => why !== null && e.preventDefault()}
    onchange={change}
  />
  <span>Reviewed</span>
  {#if why !== null}<span class="sr-only" id="{uid}-why">{why}</span>{/if}
</label>

<style>
  /* Among the header's buttons, and of their height. */
  .file-reviewed {
    border: 1px solid var(--line);
    border-radius: 5px;
    background: var(--surface);
    color: var(--fg);
    padding: 4px 8px;
    line-height: 1.3;
  }
  .file-reviewed:has(input:checked) {
    color: var(--good);
    border-color: var(--good);
    background: var(--goodbg);
  }
  .file-reviewed:not(.unavailable):hover {
    border-color: var(--accent);
  }
  .file-reviewed input {
    margin: 0;
  }
  /* Saving: a spinner on the control's corner, so the header's buttons do not move; its words are for screen readers. */
  .file-reviewed {
    position: relative;
  }
  .file-reviewed :global(.pending-tag) {
    position: absolute;
    top: -5px;
    right: -5px;
    padding: 2px;
    border-radius: 50%;
    background: var(--surface);
    font-size: 0;
    gap: 0;
  }
</style>
